import { Component, ViewChild } from '@angular/core';
import { CacheService } from '../../../cache/cache.service';
import { FilterHelper } from '../../../helpers/filter.helper';
import { InjectorHelper } from '../../../helpers/injector.helper';
import { WorkflowType } from '../../../models/enums/workflowtype.enum';
import { RequestFilter } from '../../../models/rest/requestfilter';
import { RequestOptions } from '../../../models/rest/requestoptions';
import { WorkflowModuleExecuter, WorkflowStatus } from '../../../models/workflow/workflow.model';
import { DataService } from '../../../services/data.service';
import { DataModelService, DataSourceQuery } from '../../../services/datamodel.service';
import { DynamicDataService } from '../../../services/dynamicdata.service';
import { WorkflowExitInfo, WorkflowModuleSettingsHelper, WorkflowRegistry } from '../../../services/workflow.service';
import { RequestOptionsColumn, RequestOptionsControl } from '../../../components/common/requestoptions/request.options.control';
import { DataCheck, FormulaWorkflowDialogContent } from '../../workflow.dialog';
import { WorkflowFormulaCalculator } from '../definevalue/define.value.settings';

@Component({
    selector: 'wf-deletebyfilter-settings',
    templateUrl: './deletebyfilter.settings.html',
    styleUrls: ['./deletebyfilter.settings.css']
})
export class DeleteByFilterSettings extends FormulaWorkflowDialogContent {
    static ModuleID = 'deletebyfilterWFModule';

    DataModelID: string;
    DataSource: string;
    RequestOptions = RequestOptions.CleanRequestOptions();

    @ViewChild('advanced') filterComponent: RequestOptionsControl;
    DataModels = [];
    DataSources = [];
    StaticTables = [];
    Columns = [];
    StaticDataSource = false;

    public static GetRegistry(): WorkflowRegistry {
        const reg = new WorkflowRegistry();
        reg.ID = DeleteByFilterSettings.ModuleID;
        reg.Caption = '@@DeleteItemByFilter';
        reg.GroupID = 'dataoperations';
        reg.Index = 25;
        reg.SettingsControl = DeleteByFilterSettings;
        reg.SettingsTypeHelper = new DeleteByFilterSettingsDataHelper();
        reg.Executer = DeleteByFilterModuleExecuter;
        reg.WorkflowType = WorkflowType.Client;
        return reg;
    }

    constructor(private dataModelService: DataModelService, private dataService: DataService) {
        super();
    }

    ngOnInit(): void {
        this.dataService.GetList('dynamic', 'GetAllStaticTables').subscribe((data) => {
            this.StaticTables = data;
            this.dataModelService.GetModels().subscribe(models => {
                this.DataModels = models;
                if (this.DataSource) {
                    if (data.some(x => x.Value === this.DataSource)) {
                        this.StaticDataSource = true;
                        this.fillColumns();
                    } else if (this.DataModelID) {
                        const query = new DataSourceQuery(this.DataModelID);
                        query.DBUsages = [[8192, 32]]; // RelationalWrite oder DocumentStoreWrite
                        query.IgnoreResourceBase = true;
                        query['OnlyQueryable'] = true;
                        this.dataModelService.GetContainerByDBUsage(query).subscribe((dataSources) => {
                            this.DataSources = dataSources;
                            if (dataSources.some(x => x.SID === this.DataSource)) {
                                this.fillColumns();
                            } else {
                                this.DataSource = null;
                            }
                        });
                    }
                }
            });
        });
    }

    initialize(data: any) {
        super.initialize(data);
        if (data) {
            data = JSON.parse(JSON.stringify(data));
            if (data.DataModelID) {
                this.DataModelID = data.DataModelID;
            }
            if (data.DataSource) {
                this.DataSource = data.DataSource;
            }
            if (data.Filter) {
                this.RequestOptions.Filters.push(data.Filter);
            }
        }
    }

    checkData(): DataCheck {
        const retVal = new DataCheck();
        if (!this.DataSource) {
            retVal.Error = '@@Bitte waehlen Sie eine Datenquelle.';
            retVal.IsCorrect = false;
        } else {
            if (this.StaticDataSource) {
                if (!this.StaticTables.some(x => x.Value === this.DataSource)) {
                    retVal.Error = '@@Bitte waehlen Sie eine Datenquelle.';
                    retVal.IsCorrect = false;
                }
            } else {
                if (!this.DataSources.some(x => x.SID === this.DataSource)) {
                    retVal.Error = '@@Bitte waehlen Sie eine Datenquelle.';
                    retVal.IsCorrect = false;
                }
            }
        }
        return retVal;
    }

    getResult(): any {
        const retVal = new DeleteByFilterSettingsData();
        retVal.DataSource = this.DataSource;
        if (!this.StaticTables.some(x => x.Value === this.DataSource)) {
            retVal.DataModelID = this.DataModelID;
        }
        if (this.filterComponent) {
            this.filterComponent.applyFilter();
        }
        if (this.RequestOptions.Filters && this.RequestOptions.Filters.length > 0) {
            retVal.Filter = this.RequestOptions.Filters[0];
        }
        return retVal;
    }

    modelSelected() {
        this.Columns = [];
        this.RequestOptions = RequestOptions.CleanRequestOptions();
        this.DataSources = [];
        this.DataSource = null;
        const query = new DataSourceQuery(this.DataModelID);
        query.DBUsages = [[8192, 32]]; // RelationalWrite oder DocumentStoreWrite
        query.IgnoreResourceBase = true;
        query['OnlyQueryable'] = true;
        this.dataModelService.GetContainerByDBUsage(query).subscribe((dataSources) => {
            this.DataSources = dataSources;
        });
    }

    readTableProperties() {
        this.Columns = [];
        this.RequestOptions = RequestOptions.CleanRequestOptions();
        this.fillColumns();
    }

    fillColumns() {
        if (this.DataSource) {
            CacheService.ReadFlatTable(this.DataSource, true).then((data) => {
                if (data) {
                    const cols = data['Columns'];
                    if (cols) {
                        const list = [];
                        cols.forEach((field) => {
                            const col = new RequestOptionsColumn();
                            col.Name = field.Name;
                            col.Caption = field.Name;
                            if (field.DataTyp) {
                                col.Type = field.DataTyp;
                            } else if (field.Type) {
                                col.Type = field.Type;
                            }
                            list.push(col);
                        });
                        this.Columns = list;
                    }
                }
            });
        }
    }
}

export class DeleteByFilterSettingsData {
    DataModelID: string;
    DataSource: string;
    Filter: RequestFilter;
}

export class DeleteByFilterSettingsDataHelper extends WorkflowModuleSettingsHelper {
    getExitPoints(settings: any): WorkflowExitInfo[] {
        const success = new WorkflowExitInfo();
        success.Label = '@@Success';
        const end = new WorkflowExitInfo();
        end.ID = 1;
        end.Label = '@@Error';
        return [success, end];
    }
    getEmptySettingsInstance() {
        return new DeleteByFilterSettingsData();
    }
}

export class DeleteByFilterModuleExecuter extends WorkflowModuleExecuter {
    async execute(status: WorkflowStatus): Promise<number> {
        return new Promise<number>(resolve => {
            if (status.ActualSettings) {
                if (status.ActualSettings.DataSource) {
                    const options = RequestOptions.CleanRequestOptions();
                    if (status.ActualSettings.Filter) {
                        const calculator = new WorkflowFormulaCalculator(status);
                        const filter = FilterHelper.ReplaceVariables(status.ActualSettings.Filter, (value) => {
                            return calculator.CalcFormula(value);
                        });
                        if (filter) {
                            options.Filters.push(filter);
                        }
                    }
                    const service = InjectorHelper.InjectorInstance.get<DynamicDataService>(DynamicDataService);
                    service.DeleteByFilter(status.ActualSettings.DataSource, options).subscribe(result => {
                        if (result === null) {
                            status.Logger.logError('DeleteByFilter modul: Data could not be DeleteDataWFd.');
                            resolve(1);
                        } else {
                            if (result === 0) {
                                status.Logger.logWarning('DeleteByFilter modul: No data deleted.');
                            }
                            status.ActualParameter = status.ActualSettings.DataSource;
                            resolve(0);
                        }
                    });
                    return;
                } else {
                    status.Logger.logError('DeleteByFilter modul: No DataSource set.');
                }
            } else {
                status.Logger.logError('DeleteByFilter modul: No settings found.');
            }
            resolve(super.execute(status));
        });
    }
}
